import { matchPath, Redirect, Route } from 'react-router-dom';
import Dashboard from './main-content/dashboard/Dashboard';
import AlertsContainer from './main-content/alerts/AlertsContainer';
import {
    getRuleCategoryFriendlyName,
    getRuleCategoryFromLocation,
    RuleCategory
} from '../services/RuleCategory';
import DismissAlertContainer from './main-content/alerts/dismiss/DismissAlert';
import ModifyRulesContainer from './main-content/modify-rules/ModifyRulesContainer';
import DismissedAlertsContainer from './main-content/alerts/dismiss/DismissedAlerts';
import RiskAssessmentContainer from './main-content/risk-assessment/RiskAssessmentContainer';
import AntiMoneyLaunderingMWContainer from './main-content/risk-assessment/modify-risk-assessment-rules/AntiMoneyLaunderingMW';
import FraudMWContainer from './main-content/risk-assessment/modify-risk-assessment-rules/FraudMW';
import ResponsibleGamblingMW from './main-content/risk-assessment/modify-risk-assessment-rules/ResponsibleGamblingMW';
import Players from './main-content/players/Players';
import PlayerDetails from './main-content/players/player-details/PlayerDetails';
import * as _ from 'lodash';
import React from 'react';
import { PageTitleHeader } from '../shared/headers/PageTitleHeader';
import TeamsListContainer from './main-content/team/teams/teams-list/TeamsListContainer';
import TeamDetails from './main-content/team/teams/team-details/TeamDetails';
import UserDetails from './main-content/team/user/user-details/UserDetails';
import UsersListContainer from './main-content/team/user/users-list/UsersListContainer';
import { PermissionEnum } from '../shared/models/permissions';
import ShowIfPermission from '../shared/show-if-permission/ShowIfPermission';
import { getUserDetails } from './main-content/team/user/redux/user-details/selectors';
import { getTeamDetails } from './main-content/team/teams/redux/team-details/selectors';
import HelpContainer from './main-content/help/Help';
import ClientAccessContainer from './main-content/client-access/ClientAccessContainer';
import AuditContainer from './main-content/audit/AuditContainer';
import { getSimulationRoutes } from './main-content/simulation/simulation.routes';
import { isAuditEnabled, isClientAccessEnabled } from '../shared/feature-flags';

/* Off unless the client access screen is switched on at build time. */
const clientAccessRoutes = [
    <Route
        exact
        path="/client-access"
        hideJurisdictionSelect
        component={() => (
            <ShowIfPermission permission={PermissionEnum.MANAGE_USERS}>
                <ClientAccessContainer />
            </ShowIfPermission>
        )}
        title={() => <PageTitleHeader title={'Client access'} />}
    />
];

/* Off unless the audit screen is switched on at build time. */
const auditRoutes = [
    <Route
        exact
        path="/audit"
        hideJurisdictionSelect
        component={props => (
            <ShowIfPermission permission={PermissionEnum.VIEW_AUDIT_LOG}>
                <AuditContainer {...props} />
            </ShowIfPermission>
        )}
        title={() => <PageTitleHeader title={'Audit'} />}
    />
];

export const routes = [
    <Route
        exact
        path="/dashboard"
        title={() => <PageTitleHeader title={'Dashboard'} />}
        component={Dashboard}
    />,
    <Route
        exact
        path="/"
        component={Dashboard}
        title={() => <PageTitleHeader title={'Dashboard'} />}
    />,
    <Route
        exact
        path="/alerts/anti-money-laundering"
        component={() => (
            <AlertsContainer
                ruleCategory={RuleCategory.ANTI_MONEY_LAUNDERING}
            />
        )}
        title={() => (
            <PageTitleHeader
                title={'Alerts'}
                subtitle={'Anti Money Laundering'}
            />
        )}
    />,
    <Route
        exact
        path="/alerts/anti-money-laundering/dismiss-alert"
        hideJurisdictionSelect
        title={() => (
            <PageTitleHeader title={'Alerts'} subtitle={'Dismiss Alert'} />
        )}
        component={DismissAlertContainer}
    />,
    <Route
        exact
        path="/alerts/fraud"
        title={() => <PageTitleHeader title={'Alerts'} subtitle={'Fraud'} />}
        component={() => <AlertsContainer ruleCategory={RuleCategory.FRAUD} />}
    />,
    <Route
        exact
        path="/alerts/fraud/dismiss-alert"
        hideJurisdictionSelect
        component={DismissAlertContainer}
    />,
    <Route
        exact
        path="/alerts/responsible-gambling"
        title={() => (
            <PageTitleHeader
                title={'Alerts'}
                subtitle={'Responsible Gambling'}
            />
        )}
        component={() => (
            <AlertsContainer ruleCategory={RuleCategory.RESPONSIBLE_GAMBLING} />
        )}
    />,
    <Route
        exact
        path="/alerts/responsible-gambling/dismiss-alert"
        hideJurisdictionSelect
        title={() => (
            <PageTitleHeader
                title={'Dismiss Alert'}
                subtitle={'Responsible Gambling'}
            />
        )}
        component={DismissAlertContainer}
    />,
    <Route
        path="/modify-rules"
        component={ModifyRulesContainer}
        title={({ location }) => (
            <PageTitleHeader
                title={'Modify Rules'}
                subtitle={getRuleCategoryFriendlyName(
                    getRuleCategoryFromLocation(location)
                )}
            />
        )}
    />,

    <Route
        exact
        path="/alerts/dismissed/anti-money-laundering"
        title={() => (
            <PageTitleHeader
                title={'Dismissed Alerts'}
                subtitle={'Anti Money Laundering'}
            />
        )}
        component={DismissedAlertsContainer}
    />,
    <Route
        exact
        path="/alerts/dismissed/fraud"
        title={() => (
            <PageTitleHeader title={'Dismissed Alerts'} subtitle={'Fraud'} />
        )}
        component={DismissedAlertsContainer}
    />,
    <Route
        exact
        path="/alerts/dismissed/responsible-gambling"
        title={() => (
            <PageTitleHeader
                title={'Dismissed Alerts'}
                subtitle={'Responsible Gambling'}
            />
        )}
        component={DismissedAlertsContainer}
    />,

    <Route
        exact
        path="/modify-weights/anti-money-laundering"
        title={() => (
            <PageTitleHeader
                title={'Modify Weights'}
                subtitle={'Anti Money Laundering'}
            />
        )}
        component={AntiMoneyLaunderingMWContainer}
    />,
    <Route
        exact
        path="/modify-weights/fraud"
        title={() => (
            <PageTitleHeader title={'Modify Weights'} subtitle={'Fraud'} />
        )}
        component={FraudMWContainer}
    />,
    <Route
        exact
        path="/modify-weights/responsible-gambling"
        title={() => (
            <PageTitleHeader
                title={'Modify Weights'}
                subtitle={'Responsible Gambling'}
            />
        )}
        component={ResponsibleGamblingMW}
    />,

    /* Players. */
    <Route
        exact
        path="/players"
        component={Players}
        title={() => <PageTitleHeader title={'Players'} />}
    />,
    // todo: Add selectors for player!
    <Route
        exact
        path="/players/player-details"
        component={PlayerDetails}
        title={({ state }) => (
            <PageTitleHeader
                title={'Player Details'}
                subtitle={_.get(state, 'player.data.username')}
            />
        )}
    />,
    <Route
        exact
        path="/users"
        hideJurisdictionSelect
        component={() => (
            <ShowIfPermission permission={PermissionEnum.MANAGE_USERS}>
                <UsersListContainer />
            </ShowIfPermission>
        )}
        title={() => <PageTitleHeader title={'Users'} />}
    />,
    <Route
        exact
        path="/users/details/:userId"
        component={() => (
            <ShowIfPermission permission={PermissionEnum.MANAGE_USERS}>
                <UserDetails />
            </ShowIfPermission>
        )}
        title={({ state }) => (
            <PageTitleHeader
                title={'User Details'}
                subtitle={`${(getUserDetails(state) || {}).firstName || ''} ${(
                    getUserDetails(state) || {}
                ).lastName || ''}`}
            />
        )}
    />,
    <Redirect exact from="/users/details" to="/users" />,
    <Route
        exact
        path="/teams"
        hideJurisdictionSelect
        component={() => (
            <ShowIfPermission permission={PermissionEnum.MANAGE_USERS}>
                <TeamsListContainer />
            </ShowIfPermission>
        )}
        title={() => <PageTitleHeader title={'Teams'} />}
    />,
    <Route
        exact
        path="/teams/details/:teamId"
        hideJurisdictionSelect
        component={() => (
            <ShowIfPermission permission={PermissionEnum.MANAGE_USERS}>
                <TeamDetails />
            </ShowIfPermission>
        )}
        title={({ state }) => (
            <PageTitleHeader
                title={'Team Details'}
                subtitle={getTeamDetails(state).name}
            />
        )}
    />,
    <Redirect exact from="/teams/details" to="/teams" />,
    ...(isClientAccessEnabled() ? clientAccessRoutes : []),
    ...(isAuditEnabled() ? auditRoutes : []),

    /*Risk Assessment*/
    <Redirect
        exact
        from="/risk-assessment"
        to="/risk-assessment/anti-money-laundering"
    />,
    <Route
        exact
        path="/risk-assessment/anti-money-laundering"
        component={RiskAssessmentContainer}
        title={() => (
            <PageTitleHeader
                title={'Risk'}
                subtitle={'Anti Money Laundering'}
            />
        )}
    />,
    <Route
        exact
        path="/risk-assessment/fraud"
        component={RiskAssessmentContainer}
        title={() => <PageTitleHeader title={'Risk'} subtitle={'Fraud'} />}
    />,
    <Route
        exact
        path="/risk-assessment/responsible-gambling"
        component={RiskAssessmentContainer}
        title={() => (
            <PageTitleHeader title={'Risk'} subtitle={'Responsible gambling'} />
        )}
    />,
    ...getSimulationRoutes(),

    <Route
        exact
        path="/help"
        hideJurisdictionSelect
        component={HelpContainer}
        title={() => <PageTitleHeader title={'Help'} />}
    />
];

/*
  Whether the jurisdiction and brand chosen in the header do nothing on a page, so that the header hides the choice.
  Only the pages marked `hideJurisdictionSelect` above hide it: a page that is not named here keeps it. The first route
  that matches the address decides, as in a Switch.
*/
export const hidesJurisdictionSelect = pathname => {
    const route = _.find(
        routes,
        r => r.props.path && matchPath(pathname, r.props)
    );

    return !!(route && route.props.hideJurisdictionSelect);
};
